import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { 
  ShieldCheck, ArrowLeft, ArrowRight, Check, 
  User, Building2, FileText, AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ISSUES_OPTIONS = [
  "Property was left in good condition",
  "Normal wear and tear only",
  "All keys returned",
  "Landlord never provided move-out inspection",
  "No itemized deduction statement received",
  "Deductions claimed are excessive/unjustified",
  "Landlord is unresponsive to requests"
];

const FormPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);
  
  const [formData, setFormData] = useState({
    // Step 1: Your Info & State
    tenant_name: "",
    tenant_email: "",
    tenant_phone: "",
    tenant_address: "",
    tenant_city: "",
    tenant_state: "",
    tenant_zip: "",
    property_state: "",
    
    // Landlord Info
    landlord_name: "",
    landlord_address: "",
    landlord_city: "",
    landlord_state: "",
    landlord_zip: "",
    
    // Step 2: Lease Details
    property_address: "",
    property_city: "",
    property_zip: "",
    lease_start_date: "",
    lease_end_date: "",
    move_out_date: "",
    deposit_amount: "",
    
    // Step 3: Issues & Style
    letter_style: "formal",
    issues: [],
    additional_notes: ""
  });

  useEffect(() => {
    fetchStates();
  }, []);

  const fetchStates = async () => {
    try {
      const response = await axios.get(`${API}/states`);
      setStates(response.data);
    } catch (error) {
      console.error("Failed to fetch states:", error);
      toast.error("Failed to load states");
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-fill property city/state when property_state is selected
    if (field === "property_state") {
      setFormData(prev => ({ 
        ...prev, 
        [field]: value,
        landlord_state: value 
      }));
    }
  };

  const handleIssueToggle = (issue) => {
    setFormData(prev => ({
      ...prev,
      issues: prev.issues.includes(issue) 
        ? prev.issues.filter(i => i !== issue)
        : [...prev.issues, issue]
    }));
  };

  const validateStep = (stepNum) => {
    switch (stepNum) {
      case 1:
        return formData.tenant_name && formData.tenant_email && 
               formData.property_state && formData.landlord_name;
      case 2:
        return formData.property_address && formData.deposit_amount && 
               formData.lease_start_date && formData.lease_end_date && 
               formData.move_out_date;
      case 3:
        return formData.letter_style;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(prev => prev + 1);
      window.scrollTo(0, 0);
    } else {
      toast.error("Please fill in all required fields");
    }
  };

  const handleBack = () => {
    setStep(prev => prev - 1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) {
      toast.error("Please complete all required fields");
      return;
    }

    setLoading(true);
    try {
      // Prepare data
      const submitData = {
        ...formData,
        deposit_amount: parseFloat(formData.deposit_amount),
        property_city: formData.property_city || formData.tenant_city,
        property_zip: formData.property_zip || formData.tenant_zip,
        landlord_city: formData.landlord_city || formData.property_city || formData.tenant_city,
        landlord_zip: formData.landlord_zip || formData.property_zip || formData.tenant_zip
      };

      const response = await axios.post(`${API}/letters`, submitData);
      toast.success("Letter generated successfully!");
      navigate(`/preview/${response.data.id}`);
    } catch (error) {
      console.error("Failed to create letter:", error);
      toast.error("Failed to generate letter. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getStateInfo = () => {
    if (!formData.property_state) return null;
    return states.find(s => s.code === formData.property_state);
  };

  const stateInfo = getStateInfo();

  const steps = [
    { num: 1, title: "Your Details", icon: User },
    { num: 2, title: "Lease Info", icon: Building2 },
    { num: 3, title: "Customize", icon: FileText }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
              data-testid="back-to-home-btn"
            >
              <ArrowLeft className="h-5 w-5" />
              <span className="hidden sm:inline">Back to Home</span>
            </button>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-6 w-6 text-blue-600" />
              <span className="font-serif text-lg font-bold text-slate-900">TenantShield</span>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center gap-4 sm:gap-8">
            {steps.map((s, index) => (
              <div key={s.num} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div 
                    className={`step-indicator ${
                      step > s.num ? 'completed' : 
                      step === s.num ? 'active' : 'inactive'
                    }`}
                    data-testid={`step-indicator-${s.num}`}
                  >
                    {step > s.num ? (
                      <Check className="h-5 w-5" />
                    ) : (
                      <s.icon className="h-5 w-5" />
                    )}
                  </div>
                  <span className={`text-xs mt-2 font-medium ${
                    step >= s.num ? 'text-slate-900' : 'text-slate-400'
                  }`}>
                    {s.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-12 sm:w-24 h-0.5 mx-2 ${
                    step > s.num ? 'bg-green-500' : 'bg-slate-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AnimatePresence mode="wait">
          {/* Step 1: Your Details */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-8"
            >
              <div>
                <h2 className="font-serif text-2xl font-bold text-slate-900 mb-2">
                  Let's start with your details
                </h2>
                <p className="text-slate-600">
                  We'll use this information to create your demand letter.
                </p>
              </div>

              {/* State Selection */}
              <div className="bg-white border border-slate-200 p-6 space-y-4">
                <div>
                  <Label htmlFor="property_state" className="text-sm font-semibold text-slate-700 mb-2 block">
                    Rental Property State *
                  </Label>
                  <Select 
                    value={formData.property_state} 
                    onValueChange={(value) => handleChange("property_state", value)}
                  >
                    <SelectTrigger data-testid="property-state-select" className="h-12">
                      <SelectValue placeholder="Select the state where you rented" />
                    </SelectTrigger>
                    <SelectContent>
                      {states.map(state => (
                        <SelectItem key={state.code} value={state.code}>
                          {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {stateInfo && (
                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-blue-900">
                          {stateInfo.name} Law: {stateInfo.return_days} days to return deposit
                        </p>
                        <p className="text-sm text-blue-700 mt-1">
                          {stateInfo.statute}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Your Information */}
              <div className="bg-white border border-slate-200 p-6 space-y-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <User className="h-5 w-5 text-blue-600" />
                  Your Information
                </h3>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label htmlFor="tenant_name">Full Legal Name *</Label>
                    <Input
                      id="tenant_name"
                      data-testid="tenant-name-input"
                      value={formData.tenant_name}
                      onChange={(e) => handleChange("tenant_name", e.target.value)}
                      placeholder="John Smith"
                      className="h-12"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="tenant_email">Email Address *</Label>
                    <Input
                      id="tenant_email"
                      type="email"
                      data-testid="tenant-email-input"
                      value={formData.tenant_email}
                      onChange={(e) => handleChange("tenant_email", e.target.value)}
                      placeholder="john@email.com"
                      className="h-12"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="tenant_phone">Phone (Optional)</Label>
                    <Input
                      id="tenant_phone"
                      data-testid="tenant-phone-input"
                      value={formData.tenant_phone}
                      onChange={(e) => handleChange("tenant_phone", e.target.value)}
                      placeholder="(555) 555-5555"
                      className="h-12"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <Label htmlFor="tenant_address">Current Mailing Address</Label>
                    <Input
                      id="tenant_address"
                      data-testid="tenant-address-input"
                      value={formData.tenant_address}
                      onChange={(e) => handleChange("tenant_address", e.target.value)}
                      placeholder="123 New Street, Apt 4B"
                      className="h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="tenant_city">City</Label>
                    <Input
                      id="tenant_city"
                      data-testid="tenant-city-input"
                      value={formData.tenant_city}
                      onChange={(e) => handleChange("tenant_city", e.target.value)}
                      placeholder="Los Angeles"
                      className="h-12"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="tenant_state">State</Label>
                      <Select 
                        value={formData.tenant_state} 
                        onValueChange={(value) => handleChange("tenant_state", value)}
                      >
                        <SelectTrigger data-testid="tenant-state-select" className="h-12">
                          <SelectValue placeholder="State" />
                        </SelectTrigger>
                        <SelectContent>
                          {states.map(state => (
                            <SelectItem key={state.code} value={state.code}>
                              {state.code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="tenant_zip">ZIP</Label>
                      <Input
                        id="tenant_zip"
                        data-testid="tenant-zip-input"
                        value={formData.tenant_zip}
                        onChange={(e) => handleChange("tenant_zip", e.target.value)}
                        placeholder="90001"
                        className="h-12"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Landlord Information */}
              <div className="bg-white border border-slate-200 p-6 space-y-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  Landlord Information
                </h3>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label htmlFor="landlord_name">Landlord/Property Manager Name *</Label>
                    <Input
                      id="landlord_name"
                      data-testid="landlord-name-input"
                      value={formData.landlord_name}
                      onChange={(e) => handleChange("landlord_name", e.target.value)}
                      placeholder="ABC Property Management or Jane Doe"
                      className="h-12"
                    />
                  </div>
                  
                  <div className="sm:col-span-2">
                    <Label htmlFor="landlord_address">Landlord Mailing Address</Label>
                    <Input
                      id="landlord_address"
                      data-testid="landlord-address-input"
                      value={formData.landlord_address}
                      onChange={(e) => handleChange("landlord_address", e.target.value)}
                      placeholder="456 Business Ave, Suite 100"
                      className="h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="landlord_city">City</Label>
                    <Input
                      id="landlord_city"
                      data-testid="landlord-city-input"
                      value={formData.landlord_city}
                      onChange={(e) => handleChange("landlord_city", e.target.value)}
                      placeholder="Los Angeles"
                      className="h-12"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="landlord_state">State</Label>
                      <Select 
                        value={formData.landlord_state} 
                        onValueChange={(value) => handleChange("landlord_state", value)}
                      >
                        <SelectTrigger data-testid="landlord-state-select" className="h-12">
                          <SelectValue placeholder="State" />
                        </SelectTrigger>
                        <SelectContent>
                          {states.map(state => (
                            <SelectItem key={state.code} value={state.code}>
                              {state.code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="landlord_zip">ZIP</Label>
                      <Input
                        id="landlord_zip"
                        data-testid="landlord-zip-input"
                        value={formData.landlord_zip}
                        onChange={(e) => handleChange("landlord_zip", e.target.value)}
                        placeholder="90001"
                        className="h-12"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Lease Details */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-8"
            >
              <div>
                <h2 className="font-serif text-2xl font-bold text-slate-900 mb-2">
                  Tell us about your lease
                </h2>
                <p className="text-slate-600">
                  This helps us cite the correct legal deadlines in your letter.
                </p>
              </div>

              {/* Property Details */}
              <div className="bg-white border border-slate-200 p-6 space-y-4">
                <h3 className="font-semibold text-slate-900">Rental Property Address</h3>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label htmlFor="property_address">Street Address *</Label>
                    <Input
                      id="property_address"
                      data-testid="property-address-input"
                      value={formData.property_address}
                      onChange={(e) => handleChange("property_address", e.target.value)}
                      placeholder="789 Former Apartment Rd, Unit 101"
                      className="h-12"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="property_city">City</Label>
                    <Input
                      id="property_city"
                      data-testid="property-city-input"
                      value={formData.property_city}
                      onChange={(e) => handleChange("property_city", e.target.value)}
                      placeholder="Los Angeles"
                      className="h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="property_zip">ZIP Code</Label>
                    <Input
                      id="property_zip"
                      data-testid="property-zip-input"
                      value={formData.property_zip}
                      onChange={(e) => handleChange("property_zip", e.target.value)}
                      placeholder="90001"
                      className="h-12"
                    />
                  </div>
                </div>
              </div>

              {/* Lease Dates */}
              <div className="bg-white border border-slate-200 p-6 space-y-4">
                <h3 className="font-semibold text-slate-900">Lease Dates</h3>
                
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="lease_start_date">Lease Start Date *</Label>
                    <Input
                      id="lease_start_date"
                      type="date"
                      data-testid="lease-start-date-input"
                      value={formData.lease_start_date}
                      onChange={(e) => handleChange("lease_start_date", e.target.value)}
                      className="h-12"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="lease_end_date">Lease End Date *</Label>
                    <Input
                      id="lease_end_date"
                      type="date"
                      data-testid="lease-end-date-input"
                      value={formData.lease_end_date}
                      onChange={(e) => handleChange("lease_end_date", e.target.value)}
                      className="h-12"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="move_out_date">Move-Out Date *</Label>
                    <Input
                      id="move_out_date"
                      type="date"
                      data-testid="move-out-date-input"
                      value={formData.move_out_date}
                      onChange={(e) => handleChange("move_out_date", e.target.value)}
                      className="h-12"
                    />
                  </div>
                </div>
              </div>

              {/* Deposit Amount */}
              <div className="bg-white border border-slate-200 p-6 space-y-4">
                <h3 className="font-semibold text-slate-900">Security Deposit</h3>
                
                <div>
                  <Label htmlFor="deposit_amount">Total Deposit Amount *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">$</span>
                    <Input
                      id="deposit_amount"
                      type="number"
                      data-testid="deposit-amount-input"
                      value={formData.deposit_amount}
                      onChange={(e) => handleChange("deposit_amount", e.target.value)}
                      placeholder="1500.00"
                      className="h-12 pl-8 font-mono"
                    />
                  </div>
                  <p className="text-sm text-slate-500 mt-1">
                    Enter the total security deposit you paid
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 3: Customize */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-8"
            >
              <div>
                <h2 className="font-serif text-2xl font-bold text-slate-900 mb-2">
                  Customize your letter
                </h2>
                <p className="text-slate-600">
                  Choose your letter style and add any relevant details.
                </p>
              </div>

              {/* Letter Style */}
              <div className="bg-white border border-slate-200 p-6 space-y-4">
                <h3 className="font-semibold text-slate-900">Letter Style</h3>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <button
                    type="button"
                    data-testid="style-formal-btn"
                    onClick={() => handleChange("letter_style", "formal")}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      formData.letter_style === "formal" 
                        ? "border-blue-600 bg-blue-50" 
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-slate-900">Formal Legal</span>
                      {formData.letter_style === "formal" && (
                        <Check className="h-5 w-5 text-blue-600" />
                      )}
                    </div>
                    <p className="text-sm text-slate-600">
                      Strong legal language with statute citations. Recommended for unresponsive landlords.
                    </p>
                  </button>
                  
                  <button
                    type="button"
                    data-testid="style-professional-btn"
                    onClick={() => handleChange("letter_style", "professional")}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      formData.letter_style === "professional" 
                        ? "border-blue-600 bg-blue-50" 
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-slate-900">Professional</span>
                      {formData.letter_style === "professional" && (
                        <Check className="h-5 w-5 text-blue-600" />
                      )}
                    </div>
                    <p className="text-sm text-slate-600">
                      Firm but friendly tone. Good for landlords who may have overlooked returning your deposit.
                    </p>
                  </button>
                </div>
              </div>

              {/* Issues Checklist */}
              <div className="bg-white border border-slate-200 p-6 space-y-4">
                <h3 className="font-semibold text-slate-900">Move-Out Conditions</h3>
                <p className="text-sm text-slate-600">
                  Select any that apply to strengthen your case.
                </p>
                
                <div className="space-y-3">
                  {ISSUES_OPTIONS.map((issue, index) => (
                    <label
                      key={index}
                      className="flex items-center gap-3 cursor-pointer"
                    >
                      <Checkbox
                        data-testid={`issue-checkbox-${index}`}
                        checked={formData.issues.includes(issue)}
                        onCheckedChange={() => handleIssueToggle(issue)}
                      />
                      <span className="text-slate-700">{issue}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Additional Notes */}
              <div className="bg-white border border-slate-200 p-6 space-y-4">
                <h3 className="font-semibold text-slate-900">Additional Notes (Optional)</h3>
                <Textarea
                  data-testid="additional-notes-textarea"
                  value={formData.additional_notes}
                  onChange={(e) => handleChange("additional_notes", e.target.value)}
                  placeholder="Add any other relevant details about your situation..."
                  rows={4}
                  className="resize-none"
                />
              </div>

              {/* Summary */}
              <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-3">Letter Summary</h3>
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-blue-700">Tenant:</span>
                    <span className="ml-2 text-blue-900 font-medium">{formData.tenant_name}</span>
                  </div>
                  <div>
                    <span className="text-blue-700">Landlord:</span>
                    <span className="ml-2 text-blue-900 font-medium">{formData.landlord_name}</span>
                  </div>
                  <div>
                    <span className="text-blue-700">State:</span>
                    <span className="ml-2 text-blue-900 font-medium">{stateInfo?.name || formData.property_state}</span>
                  </div>
                  <div>
                    <span className="text-blue-700">Deposit:</span>
                    <span className="ml-2 text-blue-900 font-medium font-mono">
                      ${parseFloat(formData.deposit_amount || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
          {step > 1 ? (
            <Button
              variant="outline"
              onClick={handleBack}
              data-testid="form-back-btn"
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
          ) : (
            <div />
          )}

          {step < 3 ? (
            <Button
              onClick={handleNext}
              data-testid="form-next-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
            >
              Continue <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading}
              data-testid="form-submit-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
            >
              {loading ? (
                <>
                  <div className="spinner" />
                  Generating...
                </>
              ) : (
                <>
                  Generate Letter <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

export default FormPage;
